/** @format */

import {ReactNode, useState} from 'react';

import {Box, Typography} from '@mui/material';
import 'react-datepicker/dist/react-datepicker.css';
import {Trans, useTranslation} from 'react-i18next';

import BaseSearchModal from 'components/BaseSearchModal';
import Snackbar from 'components/Snackbar';
import {TanStackTableData} from 'components/Table/core/types';
import {useCompanyContext} from 'contexts/CompanyContext';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import {FormProps} from '../../types';
import {SectionTitle} from '../../styles';
import KpiField from '../SharedFields/KpiField';
import GoalsField from '../SharedFields/GoalsField';
import AttributionModelField from '../SharedFields/AttributionModelField';
import BudgetField from '../SharedFields/BudgetField';
import {
  FormContainer,
  GridContainer,
  SubmitButton,
  OrderResearchButton,
  OrderResearchButtonContent,
  OrderResearchButtonTitle,
  OrderResearchButtonSubtext,
  NeedToConnectMetrikaBadge,
  GoToSettingButton,
} from './styles';
import {TOOLTIP_TEXTS, campaignColumns} from './constants';
import {isFormValid} from './helpers';
import {useFormState} from './hooks/useFormState';
import FieldLabelWithHelp from './components/FieldLabelWithHelp';
import DateRangeField from './components/DateRangeField';
import BalanceCallout from './components/BalanceCallout';

const Form = ({
  onSubmit,
  formDataOptions,
  purchaseState,
  remainingReports,
  onOpenPurchaseModal,
  pricingLoading,
  unitPrice,
  generateReportLoading,
  needToConnectMetrika,
}: FormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<ReactNode>('');

  const {
    state,
    resetForm,
    handlers: {
      handleCampaignChange,
      handleStartDateChange,
      handleEndDateChange,
      handleGoalChange,
      handleKpiChange,
      handleKpiAmountChange,
      handleAttributionChange,
      handleBudgetChange,
    },
  } = useFormState();

  const {campaigns, startDate, endDate, goals, kpi, kpiAmount, attributionModel, budget} = state;
  const formValid = isFormValid(state);
  const {goalOptions, campaignOptions} = formDataOptions;
  const {t} = useTranslation();
  const {currentCompany} = useCompanyContext();
  const {mixpanel} = useMixpanelContext();

  const formIsDisabled = purchaseState === 'never_purchased';
  const fieldsDisabled = needToConnectMetrika || formIsDisabled;
  const calloutState = purchaseState === 'has_balance' ? null : purchaseState;
  const purchaseButtonDisabled = pricingLoading || unitPrice <= 0;

  const handleSubmit = async () => {
    mixpanel.track('ai_report_click_order', {
      project_uuid: currentCompany?.uuid,
    });

    setIsLoading(true);
    try {
      resetForm();
      const result = await onSubmit(state);

      if (result.success) {
        setSnackbarMessage(
          <>
            <b>
              <Trans>Заявка отправлена!</Trans>
            </b>
            <br />
            <Trans>Мы собираем данные и готовим ИИ-исследование.</Trans>
            <br />
            <Trans>Уведомим, когда он будет готов.</Trans>
          </>,
        );
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(
          <Trans>Произошла ошибка при генерации отчета. Попробуйте еще раз</Trans>,
        );
        setSnackbarOpen(true);
      }
    } catch {
      setSnackbarMessage(<Trans>Произошла ошибка при генерации отчета. Попробуйте еще раз</Trans>);
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToSettings = () => {
    window.location.assign(`/home/project/edit?companyId=${currentCompany.id}`);
  };

  const orderButtonDisabled = !formValid || isLoading || generateReportLoading;
  const orderButtonLoading = isLoading || generateReportLoading;

  return (
    <FormContainer>
      <Box mb={4}>
        <SectionTitle>
          <Trans>Получить гипотезы</Trans>
        </SectionTitle>
        <Typography variant='body2' sx={{color: '#888888'}}>
          <Trans>Найдите точки роста и получите гипотезы по улучшению рекламы</Trans>
        </Typography>
      </Box>

      <GridContainer>
        <Box>
          <FieldLabelWithHelp tooltipText={TOOLTIP_TEXTS.CAMPAIGNS}>
            <Trans>Кампании</Trans>
            {needToConnectMetrika && (
              <NeedToConnectMetrikaBadge>
                <Trans>Метрика не подключена</Trans>
              </NeedToConnectMetrikaBadge>
            )}
          </FieldLabelWithHelp>
          {needToConnectMetrika ? (
            <GoToSettingButton onClick={handleGoToSettings}>
              <Trans>Перейти в настройки</Trans>
            </GoToSettingButton>
          ) : (
            <BaseSearchModal
              columns={campaignColumns}
              data={campaignOptions}
              modalTitle={t('Выбрать кампании')}
              searchPlaceholder={t('Название или ID')}
              confirmButtonText={t('Выберите кампании')}
              searchByColumnIds={['id', 'campaignName', 'accountName']}
              initialSelectedIds={campaigns}
              onConfirm={handleCampaignChange}
              disabled={fieldsDisabled}
              getDisplayLabel={(item: TanStackTableData) =>
                String(item.campaignName || item.id || '')
              }
            />
          )}
        </Box>

        <Box>
          <FieldLabelWithHelp tooltipText={TOOLTIP_TEXTS.PERIOD}>
            <Trans>Период</Trans>
          </FieldLabelWithHelp>
          <DateRangeField
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            disabled={fieldsDisabled}
          />
        </Box>

        <GoalsField
          goals={goals}
          goalOptions={goalOptions}
          onGoalChange={handleGoalChange}
          disabled={fieldsDisabled}
        />

        <KpiField
          kpi={kpi}
          kpiAmount={kpiAmount}
          onKpiChange={handleKpiChange}
          onKpiAmountChange={handleKpiAmountChange}
          disabled={fieldsDisabled}
        />

        <AttributionModelField
          attributionModel={attributionModel}
          onAttributionChange={handleAttributionChange}
          disabled={fieldsDisabled}
        />

        <BudgetField
          budget={budget}
          onBudgetChange={handleBudgetChange}
          disabled={fieldsDisabled}
        />
      </GridContainer>

      {calloutState && <BalanceCallout state={calloutState} />}

      {purchaseState === 'has_balance' ? (
        <OrderResearchButton
          onClick={handleSubmit}
          disabled={orderButtonDisabled}
          loading={orderButtonLoading}
        >
          <OrderResearchButtonContent loading={orderButtonLoading}>
            <OrderResearchButtonTitle>
              {orderButtonLoading ? (
                <Trans>Заказываем исследование</Trans>
              ) : (
                <Trans>Заказать исследование</Trans>
              )}
            </OrderResearchButtonTitle>
            <OrderResearchButtonSubtext>
              {orderButtonLoading ? (
                <Trans>Подождите</Trans>
              ) : (
                t('Доступно {{count}} исследований', {count: remainingReports})
              )}
            </OrderResearchButtonSubtext>
          </OrderResearchButtonContent>
        </OrderResearchButton>
      ) : (
        <SubmitButton onClick={onOpenPurchaseModal} disabled={purchaseButtonDisabled}>
          <Trans>Пополнить баланс ИИ-исследований</Trans>
        </SubmitButton>
      )}

      <Snackbar
        isSnackbarOpen={snackbarOpen}
        handleSnackbarClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        vertical='bottom'
        horizontal='left'
      />
    </FormContainer>
  );
};

export default Form;
